<template>
  <div class="field-flex" :style="grow ? 'flex-grow: 1' : ''">
    <div v-if="label || showCharacterLimit" class="d-flex w-100 justify-content-between">
      <label v-if="label" class="menu-sm text-color-subtle" :class="{ strongtitle: strongtitle }">{{ label }}</label>
      <span v-else></span>
      <span v-if="showCharacterLimit" class="small text-color-subtle" :class="{ 'text-danger': hasMinLengthError }"> {{ characterCount }}/{{ characterLimit }} </span>
    </div>
    <p v-if="helpText" class="help-text" v-html="formattedHelpText"></p>
    <input v-if="inputType !== 'textarea'" ref="__InputField" @blur="onBlur" @keydown="onKeydown" v-mask="maskVal" class="custom-input" :class="{'input-error': error || hasMinLengthError}"
      :style="customBorderStyle"
      :inputmode="currency ? 'numeric' : undefined"
      v-model="internalValue" :type="inputType" :placeholder="placeholder" :maxlength="characterLimit" :minlength="minLength"
      :required="isRequired" :readonly="readonly" v-bind="$attrs" />

    <textarea v-else ref="__InputField" @blur="onBlur" class="custom-input custom-textarea" :class="{'input-error': error || hasMinLengthError}" :style="customBorderStyle" v-model="internalValue" :placeholder="placeholder" :maxlength="characterLimit" :minlength="minLength"
      :required="isRequired" :readonly="readonly" v-bind="$attrs"></textarea>
    <span v-if="error" class="error-message">{{ this.errorMessage }}</span>
  </div>
</template>

<script>

function formatCurrencyInput (value) {
  const digits = String(value ?? '').replace(/\D/g, '').replace(/^0+/, '')
  if (!digits) return ''
  const cents = digits.slice(0, 9).padStart(3, '0')
  const whole = cents.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return `R$ ${whole},${cents.slice(-2)}`
}

export default {
  props: {
    label: String,
    placeholder: String,
    characterLimit: Number,
    minLength: {
      type: Number,
      default: null
    },
    showCharacterLimit: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      default: ''
    },
    customRef: String,
    inputType: {
      type: String,
      default: 'text'
    },
    required: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    mask: {
      type: Boolean,
      default: false
    },
    maskVal: {
      type: [String, Array],
      default: ''
    },
    uppercase: {
      type: Boolean,
      default: false
    },
    grow: Boolean,
    strongtitle: Boolean,
    errorMessage: {
      type: String,
      default: ''
    },
    error: {
      type: Boolean,
      default: false
    },
    helpText: {
      type: String,
      default: ''
    },
    currency: {
      type: Boolean,
      default: false
    },
    borderColor: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      internalValue: this.value
    }
  },
  methods: {
    onBlur (event) {
      const inputValue = event.target.value
      this.$emit('blur', inputValue)
    },
    onKeydown (event) {
      if (!this.currency) return
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End']
      if (allowed.includes(event.key)) return
      if (!/^\d$/.test(event.key)) event.preventDefault()
    },

    focus () {
      this.$refs.__InputField.focus()
    }
  },
  computed: {
    characterCount () {
      return this.internalValue.length
    },
    isRequired () {
      return this.required
    },
    isReadonlyVal () {
      return this.readonly
    },
    formattedHelpText () {
      // Substitui \n por <br> para permitir quebras de linha
      return this.helpText ? this.helpText.replace(/\n/g, '<br>') : ''
    },
    customBorderStyle () {
      if (this.borderColor && typeof this.borderColor === 'string' && this.borderColor.trim() !== '') {
        return { borderColor: this.borderColor + ' !important' }
      }
      return {}
    },
    hasMinLengthError () {
      if (!this.minLength || !this.internalValue) return false
      return this.internalValue.length > 0 && this.internalValue.length < this.minLength
    }
  },
  watch: {
    value (newValue) {
      this.internalValue = newValue
    },
    internalValue (newValue) {
      if (this.currency) {
        const masked = formatCurrencyInput(newValue)
        if (masked !== newValue) {
          this.internalValue = masked
          return
        }
      }
      if (this.uppercase && typeof newValue === 'string' && newValue !== newValue.toUpperCase()) {
        this.internalValue = newValue.toUpperCase()
        return
      }
      this.$emit('input', newValue)
    }
  }
}
</script>

<style scoped lang="scss">
@supports (-webkit-touch-callout: none) {
  .field-flex label, .field-flex p, .field-flex input {
    font-size: 11px !important;
  }
  .custom-input::placeholder {
    font-size: 10px !important;
  }
}

.field-flex {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
}

.custom-input {
  height: 40px !important;
  padding: 0 12px !important;
  width: 100%;
  border-radius: var(--border-radius-small, 4px) !important;
  border: 1px solid var(--stroke-heavy-contrast, #cbd5e1) !important;
  background: var(--surface-primary);
  box-shadow: none !important;
  font-weight: 400;
  font-size: 14px;
  color: var(--text-primary);

  &:disabled, &[readonly] {
    background: var(--surface-secondary);
    color: var(--text-secondary);
    cursor: not-allowed;
  }

  &:focus {
    border-color: var(--stroke-brand) !important;
    box-shadow: 0 0 0 3px rgba(0, 188, 94, 0.25) !important;
    outline: none;
  }

  &.input-error {
    border: 1px solid var(--text-danger, '#DC2626') !important;
  }
    // Estilo para autocomplete
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px var(--surface-tertiary) inset !important;
    -webkit-text-fill-color: var(--text-primary) !important;
  }
}

.custom-textarea {
  height: auto !important;
  min-height: 80px;
  padding: 12px !important;
  background: var(--surface-primary) !important;
  resize: vertical;
  font-family: Poppins, sans-serif;
  line-height: 1.5;
}

::placeholder {
  opacity: 1 !important;
  color: var(--text-detail) !important;
  font-weight: 400;
  font-size: 14px;
}

.strongtitle{
  font-size: 14px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
}

.help-text {
  color: var(--text-secondary, #475569);
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.12px;
  margin: 4px 0 8px 0;
  white-space: normal;
}

.error-message {
  margin-top: 4px;
  color: var(--text-danger, '#DC2626') !important;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: -1%;
}
</style>
